import AddIcon from "@mui/icons-material/Add";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import DownloadIcon from "@mui/icons-material/Download";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import {
  Grid,
  Pagination,
  Typography,
} from "@mui/material";
import { uniq } from "lodash";
import moment from "moment";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { flushSync } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
import CustomButton from "../../../../components/CustomButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../components/CustomTextField";
import CustomSelect from "../../../../components/Select/Select";
import {
  getPetsAllAppointmentsReportData,
  getPetsAppointmentsReportData,
} from "../../../../redux/reducers/reportSlice";
import { AppColors } from "../../../../util/AppColors";
import { w20h20BlueIcon } from "../../../../util/CommonIconStyle";
import { serviceTypes, typeList, visitTypes } from "../../../../util/dropList";
import ReportTable from "../ReportTable";
import { nameExpan } from "../tableHeaders";
import "./AppointmentHistoryReport.css";

const tableHdrs = [
  "petName",
  "vetName",
  "appointmentDateTime",
  "typeOfConsult",
  "serviceType",
  "visitType",
  "caseId",
  "system",
  "condition",
  "conditionStatus",
];
const filterByList = [
  "Appointment Date",
  "Visit Type",
  "Case ID",
  "System",
];
const dropFields = [
  "Visit Type",
  "System",
];
const initSearch = {
  petName: "",
  vetName: "",
  serviceType: [],
  typeOfConsult: [],
};
const initFilter = {
  visible: false,
  added: false,
  selectedList: [],
  values: {},
};

const AppointmentsReport = () => {
  const dispatch = useDispatch();
  const [selectedPeriod, setSelectedPeriod] = useState("week");
  const [crntTblHeders, setCrntTblHeders] = useState(tableHdrs?.slice(0, 5));
  const [activeTableHeaders, setActiveTableHeaders] = useState(
    tableHdrs?.slice(0, 5)?.map((pd, i) => ({ name: pd, isSelected: true, i })),
  );
  const [editColModVsble, setEditColModVsble] = useState(false);
  const [page, setPage] = useState(1);
  const [searchValues, setSearchValues] = useState(initSearch);
  const [filterValues, setFilterValues] = useState(initFilter);
  const { appointments, allAppointments } = useSelector(
    (state) => state?.report,
  );
  const rowsPerPage = 10;

  useEffect(() => {
    dispatch(getPetsAllAppointmentsReportData());
  }, [dispatch]);

  useEffect(() => {
    onApiCall(1);
  }, [
    selectedPeriod,
    searchValues?.petName,
    searchValues?.vetName,
    searchValues?.typeOfConsult,
    searchValues?.serviceType,
  ]);

  const historyData = appointments?.appointmentHistory?.data || appointments?.appointmentHistory || {};
  const cardsObj = historyData?.cards || {};

  const appointmentsPeriodVal =
    cardsObj?.appointments ??
    historyData?.appointmentsPeriodDisplay ??
    historyData?.appointmentsPeriod ??
    historyData?.appointmentsCount ??
    historyData?.totalAppointments ??
    "0";

  const mostCommonCond =
    cardsObj?.mostCommonCondition ??
    historyData?.mostCommonConditionDisplay ??
    historyData?.mostCommonCondition ??
    (typeof historyData?.mostCommonCondition === "object" ? historyData.mostCommonCondition?.condition : null) ??
    "N/A";

  const totalConditionsLogged =
    cardsObj?.totalConditionsLogged ??
    historyData?.totalConditionsLoggedDisplay ??
    historyData?.totalConditionsLogged ??
    historyData?.loggedConditions ??
    0;

  const summaryCards = [
    { title: "Appointments (period)", value: `${appointmentsPeriodVal}` },
    { title: "Most common condition", value: `${mostCommonCond}` },
    { title: "Total conditions logged", value: `${Number(totalConditionsLogged).toLocaleString()}` },
  ];

  const conditionsData =
    historyData?.conditions ||
    historyData?.topConditions ||
    historyData?.conditionsChart ||
    [];

  let conditionCategories = [
    "Ear Infection",
    "Skin Allergies",
    "Dental Issues",
    "Vomiting",
    "Worms or parasites",
  ];
  let conditionValues = [120, 200, 200, 250, 298];

  if (Array.isArray(conditionsData) && conditionsData.length > 0) {
    const valid = conditionsData.filter((c) => c && (c.condition || c.name || c.label));
    if (valid.length > 0) {
      conditionCategories = valid.map((c) => String(c.condition || c.name || c.label));
      conditionValues = valid.map((c) => Number(c.count ?? c.value ?? c.total ?? 0));
    } else {
      conditionCategories = [];
      conditionValues = [];
    }
  } else if (Array.isArray(conditionsData) && conditionsData.length === 0) {
    conditionCategories = [];
    conditionValues = [];
  }

  const maxVal = Math.max(...conditionValues.map((v) => (typeof v === "number" && !isNaN(v) ? v : 0)), 10);

  const conditionsChartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      events: {
        dataPointSelection: (event, chartContext, config) => {
          const selectedIndex = config?.dataPointIndex;
          if (selectedIndex >= 0 && conditionCategories[selectedIndex]) {
            const cond = conditionCategories[selectedIndex];
            setSearchValues((prev) => ({
              ...prev,
              serviceType: [{ name: cond, value: cond }],
            }));
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: "45%",
        borderRadius: 4,
      },
    },
    colors: ["#2563EB"],
    dataLabels: { enabled: false },
    xaxis: {
      categories: conditionCategories,
      min: 0,
      max: maxVal,
      tickAmount: 6,
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          fontFamily: "Montserrat, sans-serif",
          fontSize: "12px",
          colors: "#616161",
        },
      },
    },
    grid: {
      borderColor: "#e5e7eb",
      strokeDashArray: 4,
    },
  };

  const conditionsChartSeries = [
    {
      name: "Conditions",
      data: conditionValues.map((v) => (typeof v === "number" && !isNaN(v) ? v : 0)),
    },
  ];

  const handleExportExcel = () => {
    const tableData = appointments?.data ?? [];
    const exportData = tableData.map((row) => ({
      Pet: row?.petName ?? "",
      Vet: row?.vetName ?? "",
      "Date & time": row?.appointmentDateTime
        ? moment(new Date(row?.appointmentDateTime)).format("DD MMM YYYY, HH:mm")
        : "",
      "Consultation mode": row?.typeOfConsult ?? "",
      "Service type": row?.serviceType ?? "",
      "Visit type": row?.visitType ?? "",
      "Case ID": row?.caseId ?? "",
      Condition: row?.condition ?? "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Appointment History");
    XLSX.writeFile(workbook, `Appointment_History_${moment().format("YYYYMMDD")}.xlsx`);
  };

  const handleExportPDF = () => {
    window.print();
  };

  const getFilterObj = (currentPage = 1, reset = false) => {
    setPage(currentPage);
    const reqObj = {
      type: "appointments",
      page: currentPage,
      itemSize: rowsPerPage,
      period: selectedPeriod || "week",
      topN: 5,
    };
    if (reset) return reqObj;
    if (typeof searchValues?.petName === "string" && searchValues.petName.trim() !== "") {
      reqObj.petName = searchValues.petName.trim();
    } else if (Array.isArray(searchValues?.petName) && searchValues.petName.length > 0) {
      reqObj.petName = searchValues.petName;
    }
    if (typeof searchValues?.vetName === "string" && searchValues.vetName.trim() !== "") {
      reqObj.vetName = searchValues.vetName.trim();
    } else if (Array.isArray(searchValues?.vetName) && searchValues.vetName.length > 0) {
      reqObj.vetName = searchValues.vetName;
    }
    if (searchValues?.serviceType?.length > 0) {
      reqObj.serviceType = Array.isArray(searchValues.serviceType)
        ? searchValues.serviceType.map((li) => li?.value || li?.name || li)
        : searchValues.serviceType;
    }
    if (searchValues?.typeOfConsult?.length > 0) {
      reqObj.typeOfConsult = searchValues?.typeOfConsult?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Visit Type")) {
      reqObj.visitType = filterValues?.values?.["Visit Type"]?.map(
        (li) => li?.value,
      );
    }
    if (filterValues?.selectedList?.includes("Case ID")) {
      reqObj.caseId = [filterValues?.values?.["Case ID"] ?? ""];
    }
    if (filterValues?.selectedList?.includes("System")) {
      reqObj.system = filterValues?.values?.["System"]?.map((li) => li?.value);
    }
    if (filterValues?.selectedList?.includes("Appointment Date")) {
      reqObj.startDate = moment(
        new Date(filterValues?.values?.["Start Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
      reqObj.endDate = moment(
        new Date(filterValues?.values?.["End Date"] ?? new Date()),
      ).format("YYYY-MM-DD");
    }
    return reqObj;
  };

  const onApiCall = (pge = 1, reset = false) => {
    dispatch(getPetsAppointmentsReportData(getFilterObj(pge, reset)));
  };

  const handleEditColumn = () => {
    const reqList = tableHdrs?.map((pd, i) => {
      const isObjMat = activeTableHeaders?.find(
        (tblHdr) => tblHdr?.name === pd,
      );
      return { name: pd, isSelected: isObjMat?.isSelected ?? false, i };
    });
    setActiveTableHeaders(reqList);
    setEditColModVsble(true);
  };

  const onEditModClose = () => {
    setEditColModVsble(false);
  };

  const handleSelcetdColumn = (selected, ind) => {
    const reqList = activeTableHeaders?.map((act, i) => {
      if (i === ind) return { ...act, isSelected: selected };
      return act;
    });
    setActiveTableHeaders(reqList);
  };

  const handleSaveTableColumn = () => {
    const reqList = activeTableHeaders
      ?.filter((act) => act?.isSelected)
      ?.map((act) => {
        return act?.name;
      });
    setCrntTblHeders(reqList);
    setEditColModVsble(false);
  };

  const totalPage = Math.ceil((appointments?.totalRecords ?? 0) / rowsPerPage);

  const handleChangePage = (e, selectedPage) => {
    onApiCall(selectedPage);
  };

  const getTableData = () => {
    return (appointments?.data ?? [])?.map((li) => ({
      ...li,
      appointmentDateTime:
        li?.appointmentDateTime?.length > 0
          ? moment(new Date(li?.appointmentDateTime)).format(
            "DD MMM YYYY, HH:mm",
          )
          : null,
      visitType: li?.visitType === "Home" ? "Home Visit" : li?.visitType,
    }));
  };

  const handleOpenChart = () => {
    console.log("Log triggered");
  };

  const onSearchChange = (e) => {
    const { name, value } = e?.target;
    setSearchValues({
      ...searchValues,
      [name?.trim()?.replace(/,$/, "")]: value,
    });
  };

  const onReset = () => {
    flushSync(() => {
      setSearchValues(initSearch);
    });
    flushSync(() => {
      setFilterValues({ ...initFilter, selectedList: [], values: {} });
    });
    onApiCall(1, true);
  };

  const onFilterChange = (name, value) => {
    const reqObj = { ...filterValues, [name]: value };
    setFilterValues(reqObj);
  };

  const getPetList = () => {
    const reqList = (allAppointments?.data ?? [])
      ?.map((li) => li?.petName?.toLowerCase())
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getVetList = () => {
    const reqList = (allAppointments?.data ?? [])
      ?.map((li) => li?.vetName)
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const onApplyFilter = () => {
    onApiCall();
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onCloseFilter = () => {
    const reqObj = { ...filterValues, visible: false };
    setFilterValues(reqObj);
  };

  const onFilterValuesChange = (e) => {
    const { name, value } = e?.target;
    const reqObj = {
      ...filterValues,
      values: {
        ...filterValues?.values,
        [name?.trim()?.replace(/,$/, "")]: value,
      },
    };
    setFilterValues(reqObj);
  };

  const onFilterAdd = (value) => {
    const reqObj = { ...filterValues, added: value };
    setFilterValues(reqObj);
  };

  const onSelectFilter = (name) => {
    const reqObj = { ...filterValues };
    let reqList = filterValues?.selectedList;
    if (reqList?.includes(name)) {
      reqList = reqList?.filter((item) => item !== name);
    } else {
      reqList.push(name);
    }
    reqObj.selectedList = reqList;
    setFilterValues(reqObj);
  };

  const onFilterReset = () => {
    const reqObj = {
      ...initFilter,
      visible: true,
      selectedList: [],
      values: {},
    };
    setFilterValues(reqObj);
  };

  const getCaseIds = () => {
    const reqList = (allAppointments?.data ?? [])
      ?.map((li) => li?.caseId?.toString())
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    return uniqList;
  };

  const getSystems = () => {
    const reqList = (allAppointments?.data ?? [])
      ?.map((li) => li?.system?.[0] ?? "")
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const getConditions = () => {
    const reqList = (allAppointments?.data ?? [])
      ?.map((li) => li?.condition ?? "")
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const getConditionStatusList = () => {
    const reqList = (allAppointments?.data ?? [])
      ?.map((li) => li?.conditionStatus ?? "")
      ?.filter(Boolean);
    const uniqList = uniq(reqList);
    const reqDropList = uniqList?.map((li) => ({ name: li, value: li }));
    return reqDropList;
  };

  const getDropListByType = (typ) => {
    return typ === "Visit Type"
      ? visitTypes
      : typ === "Case ID"
        ? getCaseIds()
        : typ === "System"
          ? getSystems()
          : typ === "Condition"
            ? getConditions()
            : typ === "Condition Status"
              ? getConditionStatusList()
              : [];
  };

  return (
    <div className="appointment-history-container">
      {/* Title */}
      <h2 className="appointment-history-title">Appointment history</h2>

      {/* Top Filter Bar */}
      <div className="appointment-history-filter-bar">
        <div className="appointment-history-filter-left">
          <div className="w20Per">
            <CustomTextField
              options={getPetList()}
              value={searchValues?.petName ?? ""}
              name="petName"
              handleChange={onSearchChange}
              label="Pet name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w20Per ml10">
            <CustomTextField
              options={getVetList()}
              value={searchValues?.vetName ?? ""}
              name="vetName"
              handleChange={onSearchChange}
              label="Vet name"
              backgroundColor={AppColors.lightBlue2}
              fullWidth
              suggetions
            />
          </div>
          <div className="w20Per ml10">
            <CustomSelect
              list={serviceTypes}
              value={searchValues?.serviceType ?? []}
              name="serviceType"
              handleChange={onSearchChange}
              label="Service type"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
        </div>

        <div className="appointment-history-period-group">
          {[
            { label: "Week", value: "week" },
            { label: "Month", value: "month" },
            { label: "Year", value: "year" },
          ].map((item) => (
            <button
              key={item.value}
              type="button"
              className={`appointment-history-period-btn ${selectedPeriod === item.value ? "active" : ""
                }`}
              onClick={() => setSelectedPeriod(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {/* Export Action Buttons */}
      <div className="appointment-history-export-group">
        <button
          type="button"
          className="appointment-history-export-btn"
          onClick={handleExportExcel}
        >
          <DownloadIcon sx={{ fontSize: 16 }} /> Export Excel
        </button>
        <button
          type="button"
          className="appointment-history-export-btn"
          onClick={handleExportPDF}
        >
          <DownloadIcon sx={{ fontSize: 16 }} /> Export PDF
        </button>
      </div>

      {/* Summary Cards Grid (3 Cards) */}
      <div className="appointment-history-cards-grid">
        {summaryCards.map((card, idx) => (
          <div className="appointment-history-card" key={idx}>
            <h4 className="appointment-history-card-title">{card.title}</h4>
            <div className="appointment-history-card-value-container">
              <h2 className="appointment-history-card-value">{card.value}</h2>
            </div>
          </div>
        ))}
      </div>

      {/* Conditions Horizontal Bar Chart */}
      <div className="appointment-history-chart-card">
        <div className="appointment-history-chart-header">
          Conditions (click a bar to filter table)
        </div>
        <Chart
          options={conditionsChartOptions}
          series={conditionsChartSeries}
          type="bar"
          height={320}
        />
      </div>

      {/* Drill-down Header */}
      <div className="appointment-history-drilldown-header">
        Drill-down — appointment detail
      </div>

      {/* Search & Actions Bar */}
      <div className="flex-row mv10">
        <div className="top-row-cen-con w100Per">
          <div className="w15Per">
            <CustomSelect
              list={typeList}
              value={searchValues?.typeOfConsult ?? []}
              name="typeOfConsult"
              handleChange={onSearchChange}
              label="Consultation Mode"
              backgroundColor={AppColors.lightBlue2}
              multiSelectSearchCheck
            />
          </div>
          <div
            className="flex-row-ali-cen ml20 filter cursor"
            onClick={() => onFilterChange("visible", true)}
          >
            <TuneIcon sx={{ color: AppColors.appPrimary }} />
            <Typography className="font-medium fs12 blue-color ml10">
              {filterValues?.selectedList?.length > 0
                ? `${filterValues?.selectedList?.length} `
                : ""}
              Filter
            </Typography>
          </div>
          <div className="ml20">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleEditColumn}
          >
            <EditOutlinedIcon sx={w20h20BlueIcon} />
            <Typography className="blue-color fs16 font-medium ml5">
              Edit columns ({crntTblHeders?.length})
            </Typography>
          </div>
          <div
            className="flex-row-ali-cen ml20 cursor"
            onClick={handleOpenChart}
          >
            <AutoGraphIcon sx={w20h20BlueIcon} />
          </div>
        </div>
      </div>

      <Typography className="font-medium fs12 gray28 ml30">
        Number of Records: {appointments?.totalRecords ?? 0}
      </Typography>

      <div className="mtMin10">
        <ReportTable
          datas={getTableData()}
          columns={crntTblHeders}
          maxHeight="none"
        />
        {Math.ceil((appointments?.totalRecords ?? 0) / rowsPerPage) > 1 ? (
          <div className="flex1-end mt20 mb30">
            <Pagination
              count={totalPage}
              variant="outlined"
              color="primary"
              page={page}
              onChange={handleChangePage}
            />
          </div>
        ) : null}
      </div>

      {/* Edit Columns Modal */}
      <CustomModal
        open={editColModVsble}
        onClose={onEditModClose}
        header="Columns"
        modal
        modalWidth={25}
      >
        <div className="ph20 scroll-70vh">
          {activeTableHeaders?.map((hr) => (
            <div className="flex-row" key={hr?.i}>
              <CustomCheckbox
                label={nameExpan?.[hr?.name]}
                checked={hr?.isSelected}
                onChange={() => handleSelcetdColumn(!hr?.isSelected, hr?.i)}
              />
            </div>
          ))}
          <div className="flex-end mb10">
            <CustomButton
              text="Save"
              smallBtn
              onClick={handleSaveTableColumn}
            />
          </div>
        </div>
      </CustomModal>

      {/* Filter Modal */}
      <CustomModal
        open={filterValues?.visible}
        onClose={onCloseFilter}
        header="Filter"
        rightModal
        modalWidth={30}
      >
        <div className="scroll-80vh ph20">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography className="gray38 fs12 txt-regular">
                Filter by:
              </Typography>
              {!filterValues?.added ? (
                filterByList?.map((li, i) => (
                  <div className="flex-row-ali-cen" key={`${i}_li`}>
                    <CustomCheckbox
                      checked={filterValues?.selectedList?.includes(li)}
                      onChange={() => onSelectFilter(li)}
                    />
                    <Typography className="txt-regular fs14 black mb5 mlMin15">
                      {li}
                    </Typography>
                  </div>
                ))
              ) : (
                <div>
                  <div
                    className="flex-row-ali-cen gray-back4 ph10 pv10 br10 width-fit-con mt10 cursor"
                    onClick={() => onFilterAdd(false)}
                  >
                    <AddIcon
                      sx={{ color: AppColors.appPrimary, fontSize: 15 }}
                    />
                    <Typography className="font-medium fs12 blue-color ml5">
                      Add Filter
                    </Typography>
                  </div>
                  {filterValues?.selectedList?.map((li) => (
                    <div className="mt20" key={li}>
                      {dropFields?.includes(li) ? (
                        <CustomSelect
                          list={getDropListByType(li)}
                          value={filterValues?.values?.[li] ?? []}
                          name={li}
                          handleChange={onFilterValuesChange}
                          label={li}
                          multiSelectSearchCheck
                        />
                      ) : li === "Appointment Date" ? (
                        <div className="flex-row-between-align-center">
                          <div className="w45Per">
                            <CustomTextField
                              placeholder="Start Date"
                              fullWidth
                              name="Start Date"
                              handleChange={onFilterValuesChange}
                              value={
                                filterValues?.values?.["Start Date"] ?? ""
                              }
                              label="Start Date"
                              mobileDate
                            />
                          </div>
                          <div className="w45Per">
                            <CustomTextField
                              placeholder="End Date"
                              fullWidth
                              name="End Date"
                              handleChange={onFilterValuesChange}
                              value={
                                filterValues?.values?.["End Date"] ?? ""
                              }
                              label="End Date"
                              mobileDate
                            />
                          </div>
                        </div>
                      ) : (
                        <CustomTextField
                          placeholder={li}
                          fullWidth
                          name={li}
                          handleChange={onFilterValuesChange}
                          value={filterValues?.values?.[li] ?? ""}
                          label={li}
                          suggetions
                          options={getCaseIds()}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
              {!filterValues?.added ? (
                <div className="flex1-end mt15">
                  <CustomButton
                    text="Add"
                    smallBtn
                    onClick={() => onFilterAdd(true)}
                  />
                </div>
              ) : (
                <div className="flex-row-center mt15">
                  <CustomButton
                    text="Reset All"
                    smallBtn
                    onClick={onFilterReset}
                    tabSelectdBtn
                  />
                  <div className="ml20">
                    <CustomButton
                      text="Apply Filters"
                      smallBtn
                      onClick={onApplyFilter}
                    />
                  </div>
                </div>
              )}
            </Grid>
          </Grid>
        </div>
      </CustomModal>
    </div>
  );
};

export default AppointmentsReport;
